import {
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  ACTIVE_INACTIVE_FIRM,
  DELETE_FIRM,
  GET_ALL_FIRM,
  GLOBAL_DROP_DOWN,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE, LOOKUP_CATEGORY } from "../../utils/AppEnumerations";
import {
  decryptData,
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
  toastSuccess,
} from "../../utils/AppFunctions";

export interface IFirm {
  id: string;
  firm_name: string;
  contact_person_name: string | null;
  contact_person_mobile: string;
  contact_person_email: string;
  firm_type: string;
  is_active: TBitValue;
  is_deleted: TBitValue;
}
export interface IFirmAdvancedSearch {
  firm_type: string;
  is_deleted: TBitValue;
}

export const FIRM_ADVANCE_SEARCH_INITIAL_VALUE = {
  firm_type: "",
  is_deleted: BIT_VALUE.Zero,
};

export function getFirmFields<T>(
  actionComponent: (item: T) => JSX.Element,
  handleFirmStatus: (item: T) => void,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("firm_name"),
      key: "firm_name",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-3",
    },
    {
      name: t("contact_person_name"),
      key: "contact_person_name",
      sort: false,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("contact_person_mobile"),
      key: "contact_person_mobile",
      sort: false,
      type: TABLE_FIELD_TYPE.mask,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
      maskingRegex: /(\d{5})$/,
    },
    {
      name: t("contact_person_email"),
      key: "contact_person_email",
      sort: false,
      type: TABLE_FIELD_TYPE.mask,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-3",
      maskingRegex: /(?<=.).(?=[^@]*?@)|(?:@)([^@]{2})(?=.*\.)/gm,
    },
    {
      name: t("firm_type"),
      key: "firm_type",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
    },
    {
      name: t("status"),
      key: "is_active",
      sort: false,
      type: TABLE_FIELD_TYPE.switch,
      align: TABLE_FIELD_ALIGN.center,
      checkedValue: "1",
      flex: "col-span-1",
      handleCheck: handleFirmStatus,
    },
    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.center,

      flex: "col-span-2",
      component: actionComponent,
    },
  ];
}

export const getAllFirms = async (
  setFirmList: TReactSetState<IFirm[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  advanceSearch: IFirmAdvancedSearch
) => {
  try {
    const firms = await GET_ALL_FIRM(pagination, search, advanceSearch);
    if (firms && firms.code === DEFAULT_STATUS_CODE_SUCCESS) {
      const data = JSON.parse(decryptData(firms.data));
      setFirmList(data.items);
      setPagination(data.pagination);
    } else {
      setFirmList([]);
      sweetAlertError({
        message: firms.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    handleError(error, "getAllFirms");
  }
};

export const deleteFirm = async (id: string) => {
  try {
    const deleteData = await DELETE_FIRM(id);
    if (deleteData && deleteData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: deleteData.message });
      return true;
    } else {
      sweetAlertError({
        message: deleteData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "deleteFirm");
    return false;
  }
};

export const activeInactiveFirm = async (id: string, values: TBitValue) => {
  try {
    const activeInactiveData = await ACTIVE_INACTIVE_FIRM(id, {
      is_active: values,
    });
    if (
      activeInactiveData &&
      activeInactiveData.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      toastSuccess(activeInactiveData.message);
      return true;
    } else {
      sweetAlertError({
        message: activeInactiveData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "activeInactiveFirm");
    return false;
  }
};

export const getFirmTypeDdl = async (
  setFirmType: TReactSetState<IOption<string>[]>
) => {
  try {
    const payload = {
      entity: "sts_lookups",
      data_value: "data_value",
      display_value: "display_value",
      filters: [
        {
          field: "lookup_category",
          value: LOOKUP_CATEGORY.FirmType,
          condition: "eq",
        },
      ],
    };
    const firmTypeDDLList = await GLOBAL_DROP_DOWN(payload);
    if (
      firmTypeDDLList &&
      firmTypeDDLList.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      if (firmTypeDDLList.data) {
        setFirmType(firmTypeDDLList.data);
      } else {
        setFirmType([]);
      }
    } else {
      toastError(firmTypeDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getFirmTypeDdl");
  }
};
